import type { ObjectType } from '@econtractor/types';
import type { TFunction } from 'i18next';
import { DateTime as DT } from 'luxon';
import { exportReportWorkbook, moneyColumns, type ExportColumn } from 'modules/admin';
import { getBaseCurrencyCode, getCurrencyCode, getPrimaryCurrencySymbol } from 'utils/helpers/currency/currencyHelpers';
import { getObjectDebtAmount } from '../helpers/objectPayCoverage';

export interface ExportObjectsView {
    showClient: boolean;
    showTeam: boolean;
    showCreated: boolean;
    showAccess: boolean;
    showInWork: boolean;
    showDebtor: boolean;
    onlySale: boolean;
    onlyService: boolean;
}

const formatDate = (value: number): string => value ? DT.fromMillis(Number(value)).toFormat('dd.MM.yyyy') : '';

export const exportObjectsToExcel = async (objects: ObjectType[], view: ExportObjectsView, rate: number, t: TFunction): Promise<void> => {
    const isSaleLike = view.onlySale || view.onlyService;
    const columns: ExportColumn[] = [
        { key: 'name', header: t('Objects:fields.name'), width: 42 },
        { key: 'status', header: t('Objects:fields.status'), width: 24 },
    ];
    if (view.showClient) columns.push({ key: 'client', header: t('Objects:fields.client'), width: 30 });
    columns.push({ key: 'dates', header: t(isSaleLike ? 'Objects:fields.saleDate' : 'Objects:fields.date'), width: 26 });
    if (view.showTeam) columns.push({ key: 'team', header: t('Objects:fields.team'), width: 24 });
    if (view.showCreated) columns.push({ key: 'created', header: t('Objects:fields.created'), width: 26 });
    if (view.showAccess) columns.push({ key: 'access', header: t('Objects:listColumns.access'), width: 42 });
    if (view.showInWork) columns.push({ key: 'inWork', header: t('Objects:listColumns.inWork'), width: 26 });
    if (view.showDebtor) {
        columns.push(
            { key: 'paidUsd', header: `${t('Objects:fields.payments')}, ${getBaseCurrencyCode()}`, width: 20, kind: 'money' },
            { key: 'paidUah', header: `${t('Objects:fields.payments')}, ${getCurrencyCode()}`, width: 20, kind: 'money' },
            { key: 'debt', header: `${t('Objects:labelDebt')}, ${getPrimaryCurrencySymbol(rate)}`, width: 20, kind: 'money' },
        );
    }
    columns.push(...moneyColumns('price', 'priceLocal', t('Objects:fields.price'), rate));

    const rows = objects.map((object) => {
        const title = object.objectName || object.clientName || '';
        const power = object.objectPower > 0 ? ` (${object.objectPower} ${t('Objects:labelKvt')})` : '';
        const vat = object.objectVat ? ` (${t('Objects:vat')})` : '';
        const client = !view.showClient && object.clientName ? `\n${object.clientName}` : '';
        const start = formatDate(object.objectCreated);
        return {
            name: `${title}${power}${vat}${client}`,
            status: t(`Objects:status.${object.objectStatus}`),
            client: object.clientName || '',
            dates: !isSaleLike && object.objectDeadline ? `${start} — ${formatDate(object.objectDeadline)}` : start,
            team: object.teamName || '',
            created: object.userName || object.userEmail || '',
            access: (object.accessUsers || []).map((grant) => `${grant.userName} — ${t(grant.accessLevel === 'edit' ? 'common:Users.accessLevelEdit' : 'common:Users.accessLevelView')}`).join('; '),
            inWork: object.inWorkUserName || object.inWorkUserEmail || '',
            paidUsd: Number(object.paidUsd || 0),
            paidUah: Number(object.paidUah || 0),
            debt: Math.max(0, getObjectDebtAmount({ ...object, rate })),
            price: Number(object.objectPrice || 0),
            priceLocal: Number(object.objectPriceRate || 0),
        };
    });
    const title = t(view.onlyService ? 'Objects:titleServiceSalesList' : view.onlySale ? 'Objects:titleSalesList' : 'Objects:titleList');
    await exportReportWorkbook({
        filePrefix: view.onlyService ? 'service-sales' : view.onlySale ? 'sales' : 'objects',
        sheetName: title,
        title,
        headerLines: [],
        columns,
        rows,
        totalLabel: t('Reports:export.total'),
    });
};
